import React, { createRef, FC, SyntheticEvent, useEffect, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { faCalendarAlt, faAngleDoubleLeft, faAngleDoubleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  Input,
  DivDatepicker,
  DivPopup,
  ButtonMonth,
  ButtonYears,
  InputBlock,
  FontAwesomeIconCalendar,
  ButtonAngle
} from './styles';
import { TCurrentDateValue, TCurrentFilter, TDatepickerYearsSliderDirection } from '../../../../commonTypes/filters';
import { FilterLayout } from '../FilterLayout';
import { ClearButton } from '../ClearButton';
import { formatDate, getCurrentFilterValueByName, getDefaultDatepickerYears } from '../../utils';
import {
  DATEPICKER_YEARS_NUMBER,
  DATEPICKER_MIN_VALUE,
  DATEPICKER_MONTHS,
  DATEPICKER_WEEKS,
  SLIDER_DIRECTION
} from '../../const';

interface Props {
  name: string;
  currentFilterItems: TCurrentFilter[];
  onSetFilter: (data: TCurrentFilter) => void;
  onClearFilter: (data: string) => void;
  isQuick?: boolean;
}

export const Datepicker: FC<Props> = ({
  name,
  currentFilterItems,
  onSetFilter,
  onClearFilter,
  isQuick = false
}: Props) => {
  const pickerRef = createRef<Flatpickr>();
  const inputRef = createRef<HTMLInputElement>();
  const [years, setYears] = useState<number[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [isMonthsOpen, setIsMonthsOpen] = useState(false);
  const [isYearsOpen, setIsYearsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    addOnClickEvents();
    setWeekdaysStartedFromMonday();
    setYears(getDefaultDatepickerYears());
  }, []);

  useEffect(() => {
    const currentValue = getCurrentFilterValueByName(currentFilterItems, name) as TCurrentDateValue;
    if (currentValue) {
      setInputValue(`${currentValue[0]} - ${currentValue[1]}`);
      const datepicker = pickerRef.current;
      if (datepicker) {
        datepicker.flatpickr.selectedDates = [new Date(currentValue[0]), new Date(currentValue[1])];
      }
    } else {
      setInputValue('');

      const datepicker = pickerRef.current;
      if (datepicker) {
        datepicker.flatpickr.clear();
      }
    }
  }, [currentFilterItems]);

  const handleDateChange = (date: Date[]) => {
    if (date.length > 1) {
      const filterItem = {
        internalName: name,
        values: [formatDate(date[0], DATEPICKER_MONTHS), formatDate(date[1], DATEPICKER_MONTHS)] as TCurrentDateValue
      };
      onSetFilter(filterItem);
    }
  };

  const setWeekdaysStartedFromMonday = () => {
    const datepicker = pickerRef.current;
    if (datepicker) {
      datepicker.flatpickr.l10n.firstDayOfWeek = 1;
      datepicker.flatpickr.weekdayContainer.innerHTML = DATEPICKER_WEEKS;
      datepicker.flatpickr.redraw();
    }
  };

  const addOnClickEvents = () => {
    const datepicker = pickerRef.current;
    if (datepicker) {
      datepicker.flatpickr.yearElements[0].onclick = () => {
        setIsYearsOpen(true);
      };
      datepicker.flatpickr.monthElements[0].onclick = () => {
        setIsMonthsOpen(true);
      };
    }
  };

  const changeMonth = (e: SyntheticEvent) => {
    const target = e.target as HTMLButtonElement;
    const month = target.id.slice(5);
    const datepicker = pickerRef.current;
    if (datepicker) {
      datepicker.flatpickr.changeMonth(+month - datepicker.flatpickr.currentMonth);
      setCurrentMonth(+month);
      setIsMonthsOpen(false);
    }
  };

  const changeYear = (e: SyntheticEvent) => {
    const target = e.target as HTMLButtonElement;
    const year = target.id.slice(4);
    const datepicker = pickerRef.current;
    if (datepicker) {
      datepicker.flatpickr.changeYear(+year);
      setCurrentYear(+year);
      setIsYearsOpen(false);
    }
  };

  const handleClear = () => {
    const datepicker = pickerRef.current;
    if (datepicker) {
      datepicker.flatpickr.clear();
      setIsMonthsOpen(false);
      setIsYearsOpen(false);
      onClearFilter(name);
    }
  };

  const moveYears = (direction: TDatepickerYearsSliderDirection) => {
    const newYears =
      direction === SLIDER_DIRECTION.LEFT
        ? years.map((year) => year - DATEPICKER_YEARS_NUMBER)
        : years.map((year) => year + DATEPICKER_YEARS_NUMBER);
    setYears([...newYears]);
  };

  const handleSliderLeftClick = () => {
    moveYears(SLIDER_DIRECTION.LEFT);
  };

  const handleSliderRightClick = () => {
    moveYears(SLIDER_DIRECTION.RIGHT);
  };

  return (
    <>
      <FilterLayout>
        <DivDatepicker>
          <InputBlock>
            <FontAwesomeIconCalendar icon={faCalendarAlt} />
            <Input type="text" readOnly={true} ref={inputRef} value={inputValue} />
          </InputBlock>
          <DivPopup isOpen={isMonthsOpen}>
            {DATEPICKER_MONTHS.map((month, index) => (
              <ButtonMonth isActive={currentMonth === index} key={month} id={`month${index}`} onClick={changeMonth}>
                {month}
              </ButtonMonth>
            ))}
          </DivPopup>
          <DivPopup isOpen={isYearsOpen}>
            <ButtonAngle onClick={handleSliderLeftClick}>
              <FontAwesomeIcon icon={faAngleDoubleLeft} size="2x" />
            </ButtonAngle>
            {years.map((year: number) => (
              <ButtonYears isActive={+currentYear === year} key={year} id={`year${year}`} onClick={changeYear}>
                {year}
              </ButtonYears>
            ))}
            <ButtonAngle onClick={handleSliderRightClick}>
              <FontAwesomeIcon icon={faAngleDoubleRight} size="2x" />
            </ButtonAngle>
          </DivPopup>
          <div>
            <Flatpickr
              ref={pickerRef}
              onChange={handleDateChange}
              options={{
                enableTime: false,
                inline: true,
                mode: 'range',
                altInput: true,
                altFormat: 'M j, Y',
                altInputClass: 'datepicker',
                locale: 'en',
                dateFormat: 'm-d-Y',
                monthSelectorType: 'static',
                disable: [(date) => date > new Date() || date < new Date(DATEPICKER_MIN_VALUE)]
              }}
            />
          </div>
        </DivDatepicker>
        {!isQuick && <ClearButton onClear={handleClear} />}
      </FilterLayout>
    </>
  );
};
